import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DocumentManagement = ({ documents }) => {
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] = useState(null);

  const documentTypes = [
    {
      id: 'identity',
      title: 'Identity Document',
      description: 'Government-issued photo ID (Passport, Driver\'s License, National ID)',
      status: documents?.identity?.status || 'verified',
      uploadDate: documents?.identity?.uploadDate || '2024-11-15',
      expiryDate: documents?.identity?.expiryDate || '2029-06-15',
      fileName: documents?.identity?.fileName || 'passport_john_smith.pdf',
      fileSize: documents?.identity?.fileSize || '2.4 MB',
      thumbnail: documents?.identity?.thumbnail || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop',
      thumbnailAlt: 'Scanned passport document showing photo page with personal information'
    },
    {
      id: 'address',
      title: 'Proof of Address',
      description: 'Utility bill, bank statement, or lease agreement (not older than 3 months)',
      status: documents?.address?.status || 'verified',
      uploadDate: documents?.address?.uploadDate || '2024-11-16',
      expiryDate: documents?.address?.expiryDate || null,
      fileName: documents?.address?.fileName || 'utility_bill_november_2024.pdf',
      fileSize: documents?.address?.fileSize || '1.8 MB',
      thumbnail: documents?.address?.thumbnail || 'https://images.unsplash.com/photo-1554224154-26032fced8bd?w=400&h=300&fit=crop',
      thumbnailAlt: 'Utility bill document showing address and billing information'
    },
    {
      id: 'selfie',
      title: 'Selfie Verification',
      description: 'Clear photo of yourself holding your ID document',
      status: documents?.selfie?.status || 'pending',
      uploadDate: documents?.selfie?.uploadDate || '2024-11-17',
      expiryDate: documents?.selfie?.expiryDate || null,
      fileName: documents?.selfie?.fileName || 'selfie_verification.jpg',
      fileSize: documents?.selfie?.fileSize || '3.2 MB',
      thumbnail: documents?.selfie?.thumbnail || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      thumbnailAlt: 'Professional headshot of man with short brown hair in business attire for identity verification'
    },
    {
      id: 'income',
      title: 'Income Verification',
      description: 'Pay stub, employment letter, or tax return (optional for higher limits)',
      status: documents?.income?.status || 'not_uploaded',
      uploadDate: documents?.income?.uploadDate || null,
      expiryDate: documents?.income?.expiryDate || null,
      fileName: documents?.income?.fileName || null,
      fileSize: documents?.income?.fileSize || null,
      thumbnail: null,
      thumbnailAlt: null
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'rejected':
        return 'text-error bg-error/10 border-error/20';
      case 'not_uploaded':
        return 'text-muted-foreground bg-muted border-border';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'rejected':
        return 'XCircle';
      case 'not_uploaded':
        return 'Upload';
      default:
        return 'FileText';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'pending':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      case 'not_uploaded':
        return 'Not Uploaded';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
  };

  const handleCloseModal = () => {
    setSelectedDocument(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Document Management</h2>
            <p className="text-sm text-muted-foreground">View and manage your uploaded verification documents</p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/document-upload')}
          iconName="Upload"
          iconPosition="left"
          iconSize={16}
        >
          Upload Documents
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {documentTypes?.map((doc) => (
          <div key={doc?.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-medium text-foreground mb-1">{doc?.title}</h3>
                <p className="text-sm text-muted-foreground">{doc?.description}</p>
              </div>
              <div className={`px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(doc?.status)}`}>
                <div className="flex items-center space-x-1">
                  <Icon name={getStatusIcon(doc?.status)} size={12} />
                  <span>{getStatusText(doc?.status)}</span>
                </div>
              </div>
            </div>

            {doc?.status !== 'not_uploaded' ? (
              <div className="space-y-3">
                {doc?.thumbnail && (
                  <div className="w-full h-32 bg-muted rounded-lg overflow-hidden">
                    <Image
                      src={doc?.thumbnail}
                      alt={doc?.thumbnailAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">File Name:</span>
                    <span className="font-medium text-foreground truncate ml-2">{doc?.fileName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">File Size:</span>
                    <span className="font-medium text-foreground">{doc?.fileSize}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Upload Date:</span>
                    <span className="font-medium text-foreground">{formatDate(doc?.uploadDate)}</span>
                  </div>
                  {doc?.expiryDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Expires:</span>
                      <span className="font-medium text-foreground">{formatDate(doc?.expiryDate)}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDocument(doc)}
                    iconName="Eye"
                    iconPosition="left"
                    iconSize={14}
                  >
                    View
                  </Button>
                  {(doc?.status === 'rejected' || doc?.status === 'verified') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/document-upload')}
                      iconName="Upload"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Re-upload
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">No document uploaded</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/document-upload')}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={14}
                >
                  Upload Now
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Document Preview Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">{selectedDocument?.title}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseModal}
                iconName="X"
                iconSize={16}
              />
            </div>
            <div className="p-4">
              {selectedDocument?.thumbnail && (
                <div className="w-full h-96 bg-muted rounded-lg overflow-hidden mb-4">
                  <Image
                    src={selectedDocument?.thumbnail}
                    alt={selectedDocument?.thumbnailAlt}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ml-2 ${getStatusColor(selectedDocument?.status)}`}>
                    <Icon name={getStatusIcon(selectedDocument?.status)} size={12} />
                    <span>{getStatusText(selectedDocument?.status)}</span>
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Upload Date:</span>
                  <span className="font-medium text-foreground ml-2">{formatDate(selectedDocument?.uploadDate)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">File Size:</span>
                  <span className="font-medium text-foreground ml-2">{selectedDocument?.fileSize}</span>
                </div>
                {selectedDocument?.expiryDate && (
                  <div>
                    <span className="text-muted-foreground">Expires:</span>
                    <span className="font-medium text-foreground ml-2">{formatDate(selectedDocument?.expiryDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;